import { BaseService } from './BaseService';

// Create API response helper
const createApiResponse = (data, message = 'Success') => ({
  success: true,
  data,
  message,
  timestamp: new Date().toISOString()
});

export class AgentService extends BaseService {
  constructor() {
    super();
  }

  // Assignment Methods
  async getAssignmentMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Unassigned Tickets',
          value: '24',
          status: 'warning',
          trend: 'up',
          change: '+3'
        },
        {
          name: 'Auto Assignments',
          value: '156',
          status: 'good',
          trend: 'up',
          change: '+12'
        },
        {
          name: 'Manual Assignments',
          value: '89',
          status: 'good',
          trend: 'down',
          change: '-5'
        },
        {
          name: 'Avg Assignment Time',
          value: '2.3 min',
          status: 'good',
          trend: 'down',
          change: '-0.4 min'
        }
      ],
      assignmentStats: [
        {
          type: 'auto',
          name: 'Auto Assignment',
          count: 156,
          percentage: 63.7
        },
        {
          type: 'manual',
          name: 'Manual Assignment',
          count: 89,
          percentage: 36.3
        }
      ],
      recentAssignments: [
        {
          ticketId: '12345',
          agentName: 'John Smith',
          method: 'auto',
          priority: 'high',
          timestamp: '2 minutes ago'
        },
        {
          ticketId: '12344',
          agentName: 'Sarah Johnson',
          method: 'manual',
          priority: 'medium',
          timestamp: '5 minutes ago'
        },
        {
          ticketId: '12343',
          agentName: 'Mike Wilson',
          method: 'auto',
          priority: 'low',
          timestamp: '8 minutes ago'
        }
      ]
    };
    
    return createApiResponse(metrics, 'Assignment metrics retrieved successfully');
  }

  async getUnassignedTickets() {
    await this.simulateDelay();
    
    const tickets = [
      {
        id: '12350',
        subject: 'Unable to access company email',
        priority: 'high',
        status: 'open',
        createdAt: '2025-01-15 14:30:15',
        customer: 'john.doe@company.com',
        department: 'IT Support'
      },
      {
        id: '12349',
        subject: 'Password reset request',
        priority: 'medium',
        status: 'open',
        createdAt: '2025-01-15 14:25:42',
        customer: 'jane.smith@company.com',
        department: 'IT Support'
      },
      {
        id: '12348',
        subject: 'Software installation issue',
        priority: 'low',
        status: 'open',
        createdAt: '2025-01-15 14:20:18',
        customer: 'bob.wilson@company.com',
        department: 'IT Support'
      },
      {
        id: '12347',
        subject: 'Network connectivity problems',
        priority: 'critical',
        status: 'open',
        createdAt: '2025-01-15 14:15:33',
        customer: 'alice.brown@company.com',
        department: 'IT Support'
      },
      {
        id: '12346',
        subject: 'Printer not working',
        priority: 'medium',
        status: 'open',
        createdAt: '2025-01-15 14:10:07',
        customer: 'charlie.davis@company.com',
        department: 'IT Support'
      }
    ];
    
    return createApiResponse(tickets, 'Unassigned tickets retrieved successfully');
  }

  async getAssignmentRules() {
    await this.simulateDelay();
    
    const rules = [
      {
        id: 'rule-001',
        name: 'Priority-Based Assignment',
        description: 'Assign critical tickets to senior agents',
        enabled: true,
        priority: 'high',
        conditions: ['priority = critical', 'agent_level >= senior'],
        actions: ['assign_to_senior_agent']
      },
      {
        id: 'rule-002',
        name: 'Department Routing',
        description: 'Route tickets to appropriate departments',
        enabled: true,
        priority: 'medium',
        conditions: ['department = IT Support'],
        actions: ['route_to_it_support']
      },
      {
        id: 'rule-003',
        name: 'Workload Balancing',
        description: 'Distribute tickets evenly among available agents',
        enabled: true,
        priority: 'low',
        conditions: ['agent_status = available'],
        actions: ['assign_to_least_busy']
      },
      {
        id: 'rule-004',
        name: 'Skill-Based Assignment',
        description: 'Assign tickets based on agent skills',
        enabled: false,
        priority: 'medium',
        conditions: ['agent_skills contains ticket_category'],
        actions: ['assign_to_skilled_agent']
      }
    ];
    
    return createApiResponse(rules, 'Assignment rules retrieved successfully');
  }

  async getAgentWorkload() {
    await this.simulateDelay();
    
    const workload = [
      {
        id: 'agent-001',
        name: 'John Smith',
        department: 'IT Support',
        status: 'available',
        currentTickets: 8,
        maxCapacity: 15,
        utilization: 53,
        avgResponseTime: '2.1 min',
        resolutionRate: 94
      },
      {
        id: 'agent-002',
        name: 'Sarah Johnson',
        department: 'IT Support',
        status: 'busy',
        currentTickets: 12,
        maxCapacity: 15,
        utilization: 80,
        avgResponseTime: '3.2 min',
        resolutionRate: 89
      },
      {
        id: 'agent-003',
        name: 'Mike Wilson',
        department: 'IT Support',
        status: 'available',
        currentTickets: 5,
        maxCapacity: 15,
        utilization: 33,
        avgResponseTime: '1.8 min',
        resolutionRate: 96
      },
      {
        id: 'agent-004',
        name: 'Lisa Brown',
        department: 'IT Support',
        status: 'busy',
        currentTickets: 14,
        maxCapacity: 15,
        utilization: 93,
        avgResponseTime: '4.1 min',
        resolutionRate: 87
      },
      {
        id: 'agent-005',
        name: 'David Lee',
        department: 'IT Support',
        status: 'available',
        currentTickets: 6,
        maxCapacity: 15,
        utilization: 40,
        avgResponseTime: '2.3 min',
        resolutionRate: 92
      }
    ];
    
    return createApiResponse(workload, 'Agent workload retrieved successfully');
  }

  async autoAssignTickets() {
    await this.simulateDelay(2000);
    
    const result = {
      assignedTickets: 12,
      failedAssignments: 2,
      assignments: [
        {
          ticketId: '12350',
          agentId: 'agent-001',
          agentName: 'John Smith',
          method: 'auto',
          timestamp: new Date().toISOString()
        },
        {
          ticketId: '12349',
          agentId: 'agent-003',
          agentName: 'Mike Wilson',
          method: 'auto',
          timestamp: new Date().toISOString()
        }
      ]
    };
    
    return createApiResponse(result, 'Auto assignment completed successfully');
  }

  async assignTickets(ticketIds, agentId) {
    await this.simulateDelay();
    
    const agent = {
      'agent-001': 'John Smith',
      'agent-002': 'Sarah Johnson',
      'agent-003': 'Mike Wilson',
      'agent-004': 'Lisa Brown',
      'agent-005': 'David Lee'
    };
    
    const result = {
      assignedTickets: ticketIds.length,
      agentId,
      agentName: agent[agentId],
      assignments: ticketIds.map(ticketId => ({
        ticketId,
        agentId,
        agentName: agent[agentId],
        method: 'manual',
        timestamp: new Date().toISOString()
      }))
    };
    
    return createApiResponse(result, 'Tickets assigned successfully');
  }

  // Ticket Management Methods
  async getAgentTickets(agentId) {
    await this.simulateDelay();
    
    const tickets = [
      {
        id: '12345',
        subject: 'Email access issue',
        priority: 'high',
        status: 'in_progress',
        createdAt: '2025-01-15 10:30:15',
        customer: 'customer1@company.com',
        assignedAgent: agentId
      },
      {
        id: '12344',
        subject: 'Password reset',
        priority: 'medium',
        status: 'open',
        createdAt: '2025-01-15 11:15:42',
        customer: 'customer2@company.com',
        assignedAgent: agentId
      }
    ];
    
    return createApiResponse(tickets, 'Agent tickets retrieved successfully');
  }

  async updateTicketStatus(ticketId, status) {
    await this.simulateDelay();
    
    const result = {
      ticketId,
      status,
      updatedAt: new Date().toISOString(),
      updatedBy: 'agent'
    };
    
    return createApiResponse(result, 'Ticket status updated successfully');
  }

  // Knowledge Base Methods
  async getKnowledgeArticles() {
    await this.simulateDelay();
    
    const articles = [
      {
        id: 'kb-001',
        title: 'How to reset your password',
        category: 'Account Management',
        views: 1250,
        helpful: 89,
        lastUpdated: '2025-01-10'
      },
      {
        id: 'kb-002',
        title: 'Email configuration guide',
        category: 'Email Support',
        views: 890,
        helpful: 76,
        lastUpdated: '2025-01-12'
      }
    ];
    
    return createApiResponse(articles, 'Knowledge articles retrieved successfully');
  }

  async createKnowledgeArticle(article) {
    await this.simulateDelay();
    
    const result = {
      ...article,
      id: `kb-${Date.now()}`,
      createdAt: new Date().toISOString(),
      createdBy: 'agent',
      status: 'draft'
    };
    
    return createApiResponse(result, 'Knowledge article created successfully');
  }

  // Performance Methods
  async getAgentPerformance(agentId) {
    await this.simulateDelay();
    
    const performance = {
      agentId,
      period: '30 days',
      metrics: {
        ticketsResolved: 45,
        avgResolutionTime: '2.3 hours',
        customerSatisfaction: 4.2,
        responseTime: '15 minutes'
      },
      trends: {
        ticketsResolved: '+12%',
        avgResolutionTime: '-8%',
        customerSatisfaction: '+0.3',
        responseTime: '-2 minutes'
      }
    };
    
    return createApiResponse(performance, 'Agent performance retrieved successfully');
  }

  async getAgentReports(agentId, period = '30d') {
    await this.simulateDelay();
    
    const reports = {
      agentId,
      period,
      summary: {
        totalTickets: 67,
        resolvedTickets: 45,
        pendingTickets: 12,
        escalatedTickets: 3
      },
      performance: {
        resolutionRate: 67.2,
        avgResolutionTime: '2.3 hours',
        customerRating: 4.2,
        firstCallResolution: 78.5
      }
    };
    
    return createApiResponse(reports, 'Agent reports retrieved successfully');
  }

  // Escalation Methods
  async getEscalationMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Active Escalations',
          value: '12',
          status: 'warning',
          trend: 'up',
          change: '+3'
        },
        {
          name: 'Escalation Rate',
          value: '8.5%',
          status: 'warning',
          trend: 'up',
          change: '+1.2%'
        },
        {
          name: 'Avg Escalation Time',
          value: '4.2 hours',
          status: 'good',
          trend: 'down',
          change: '-0.8 hours'
        },
        {
          name: 'Resolution Rate',
          value: '92%',
          status: 'good',
          trend: 'up',
          change: '+3%'
        }
      ],
      escalationLevels: [
        {
          level: 'level1',
          name: 'Level 1 - Supervisor',
          count: 5,
          percentage: 41.7
        },
        {
          level: 'level2',
          name: 'Level 2 - Manager',
          count: 4,
          percentage: 33.3
        },
        {
          level: 'level3',
          name: 'Level 3 - Director',
          count: 3,
          percentage: 25.0
        }
      ],
      recentEscalations: [
        {
          ticketId: '12345',
          level: 'level2',
          reason: 'Customer not satisfied with resolution',
          timestamp: '2 hours ago'
        },
        {
          ticketId: '12344',
          level: 'level1',
          reason: 'SLA breach imminent',
          timestamp: '4 hours ago'
        },
        {
          ticketId: '12343',
          level: 'level3',
          reason: 'Critical system issue',
          timestamp: '6 hours ago'
        }
      ]
    };
    
    return createApiResponse(metrics, 'Escalation metrics retrieved successfully');
  }

  async getEscalatedTickets() {
    await this.simulateDelay();
    
    const tickets = [
      {
        id: '12350',
        subject: 'Critical system outage affecting multiple users',
        priority: 'critical',
        escalationLevel: 'level3',
        escalatedBy: 'John Smith',
        escalatedAt: '2025-01-15 14:30:15',
        reason: 'System-wide outage requiring immediate attention'
      },
      {
        id: '12349',
        subject: 'Customer complaint about service quality',
        priority: 'high',
        escalationLevel: 'level2',
        escalatedBy: 'Sarah Johnson',
        escalatedAt: '2025-01-15 14:25:42',
        reason: 'Customer not satisfied with initial resolution'
      },
      {
        id: '12348',
        subject: 'SLA breach imminent - urgent resolution needed',
        priority: 'high',
        escalationLevel: 'level1',
        escalatedBy: 'Mike Wilson',
        escalatedAt: '2025-01-15 14:20:18',
        reason: 'Ticket approaching SLA deadline'
      },
      {
        id: '12347',
        subject: 'Complex technical issue requiring expert knowledge',
        priority: 'medium',
        escalationLevel: 'level2',
        escalatedBy: 'Lisa Brown',
        escalatedAt: '2025-01-15 14:15:33',
        reason: 'Beyond current agent expertise level'
      },
      {
        id: '12346',
        subject: 'VIP customer request for priority handling',
        priority: 'high',
        escalationLevel: 'level1',
        escalatedBy: 'David Lee',
        escalatedAt: '2025-01-15 14:10:07',
        reason: 'VIP customer requires immediate attention'
      }
    ];
    
    return createApiResponse(tickets, 'Escalated tickets retrieved successfully');
  }

  async getEscalationRules() {
    await this.simulateDelay();
    
    const rules = [
      {
        id: 'rule-001',
        name: 'SLA Breach Escalation',
        description: 'Automatically escalate tickets approaching SLA deadline',
        enabled: true,
        priority: 'high',
        trigger: 'SLA deadline < 2 hours',
        targetLevel: 'level1',
        conditions: ['sla_deadline_approaching', 'priority != low'],
        actions: ['notify_supervisor', 'escalate_to_level1']
      },
      {
        id: 'rule-002',
        name: 'Customer Dissatisfaction Escalation',
        description: 'Escalate tickets with negative customer feedback',
        enabled: true,
        priority: 'medium',
        trigger: 'Customer rating < 3 stars',
        targetLevel: 'level2',
        conditions: ['customer_rating_low', 'feedback_negative'],
        actions: ['notify_manager', 'escalate_to_level2']
      },
      {
        id: 'rule-003',
        name: 'Critical Priority Escalation',
        description: 'Immediately escalate critical priority tickets',
        enabled: true,
        priority: 'high',
        trigger: 'Priority = Critical',
        targetLevel: 'level3',
        conditions: ['priority_critical'],
        actions: ['notify_director', 'escalate_to_level3']
      },
      {
        id: 'rule-004',
        name: 'VIP Customer Escalation',
        description: 'Escalate tickets from VIP customers',
        enabled: false,
        priority: 'medium',
        trigger: 'Customer type = VIP',
        targetLevel: 'level1',
        conditions: ['customer_type_vip'],
        actions: ['notify_supervisor', 'escalate_to_level1']
      }
    ];
    
    return createApiResponse(rules, 'Escalation rules retrieved successfully');
  }

  async getEscalationHistory() {
    await this.simulateDelay();
    
    const history = [
      {
        ticketId: '12350',
        action: 'escalated',
        level: 'level3',
        reason: 'Critical system issue requiring immediate attention',
        userName: 'John Smith',
        timestamp: '2025-01-15 14:30:15'
      },
      {
        ticketId: '12349',
        action: 'escalated',
        level: 'level2',
        reason: 'Customer not satisfied with resolution',
        userName: 'Sarah Johnson',
        timestamp: '2025-01-15 14:25:42'
      },
      {
        ticketId: '12348',
        action: 'de-escalated',
        level: 'level1',
        reason: 'Issue resolved at current level',
        userName: 'Mike Wilson',
        timestamp: '2025-01-15 14:20:18'
      },
      {
        ticketId: '12347',
        action: 'escalated',
        level: 'level2',
        reason: 'Complex technical issue beyond agent expertise',
        userName: 'Lisa Brown',
        timestamp: '2025-01-15 14:15:33'
      },
      {
        ticketId: '12346',
        action: 'escalated',
        level: 'level1',
        reason: 'VIP customer requires priority handling',
        userName: 'David Lee',
        timestamp: '2025-01-15 14:10:07'
      }
    ];
    
    return createApiResponse(history, 'Escalation history retrieved successfully');
  }

  async escalateTicket(ticketId, level, reason) {
    await this.simulateDelay();
    
    const result = {
      ticketId,
      escalationLevel: level,
      reason,
      escalatedAt: new Date().toISOString(),
      escalatedBy: 'current_agent',
      status: 'escalated'
    };
    
    return createApiResponse(result, 'Ticket escalated successfully');
  }

  async deEscalateTicket(ticketId) {
    await this.simulateDelay();
    
    const result = {
      ticketId,
      deEscalatedAt: new Date().toISOString(),
      deEscalatedBy: 'current_agent',
      status: 'de-escalated'
    };
    
    return createApiResponse(result, 'Ticket de-escalated successfully');
  }

  // Resolution Methods
  async getResolutionMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Total Resolutions',
          value: '1,247',
          status: 'good',
          trend: 'up',
          change: '+89'
        },
        {
          name: 'Avg Resolution Time',
          value: '2.3 hours',
          status: 'good',
          trend: 'down',
          change: '-0.4 hours'
        },
        {
          name: 'First Call Resolution',
          value: '78%',
          status: 'good',
          trend: 'up',
          change: '+3%'
        },
        {
          name: 'Customer Satisfaction',
          value: '4.2/5',
          status: 'good',
          trend: 'up',
          change: '+0.2'
        }
      ],
      qualityDistribution: [
        {
          level: 'excellent',
          count: 456,
          percentage: 36.6
        },
        {
          level: 'good',
          count: 523,
          percentage: 41.9
        },
        {
          level: 'fair',
          count: 201,
          percentage: 16.1
        },
        {
          level: 'poor',
          count: 67,
          percentage: 5.4
        }
      ],
      recentResolutions: [
        {
          ticketId: '12345',
          resolution: 'Password reset completed successfully',
          quality: 'excellent',
          timestamp: '2 hours ago'
        },
        {
          ticketId: '12344',
          resolution: 'Email configuration updated',
          quality: 'good',
          timestamp: '4 hours ago'
        },
        {
          ticketId: '12343',
          resolution: 'Software installation issue resolved',
          quality: 'fair',
          timestamp: '6 hours ago'
        }
      ]
    };
    
    return createApiResponse(metrics, 'Resolution metrics retrieved successfully');
  }

  async getResolutionTemplates() {
    await this.simulateDelay();
    
    const templates = [
      {
        id: 'template-001',
        name: 'Password Reset',
        category: 'account',
        description: 'Standard password reset procedure for user accounts',
        content: 'I have successfully reset your password. Please check your email for the new login credentials. If you have any issues accessing your account, please let me know.',
        usageCount: 156
      },
      {
        id: 'template-002',
        name: 'Email Configuration',
        category: 'technical',
        description: 'Email client configuration assistance',
        content: 'I have configured your email settings. Please restart your email client and test the connection. The settings should now work properly.',
        usageCount: 89
      },
      {
        id: 'template-003',
        name: 'Software Installation',
        category: 'technical',
        description: 'Software installation troubleshooting',
        content: 'I have resolved the software installation issue. The application should now install and run correctly. Please test the functionality and let me know if you encounter any problems.',
        usageCount: 67
      },
      {
        id: 'template-004',
        name: 'Network Connectivity',
        category: 'technical',
        description: 'Network connection troubleshooting',
        content: 'I have identified and resolved the network connectivity issue. Your internet connection should now be stable. Please test your connection and let me know if you experience any further issues.',
        usageCount: 45
      },
      {
        id: 'template-005',
        name: 'Account Access',
        category: 'account',
        description: 'Account access restoration',
        content: 'I have restored access to your account. You should now be able to log in normally. Please test your login and contact me if you need any further assistance.',
        usageCount: 34
      },
      {
        id: 'template-006',
        name: 'Data Recovery',
        category: 'data',
        description: 'Data recovery and restoration',
        content: 'I have successfully recovered your data. The files have been restored to their original location. Please verify that all your data is intact and accessible.',
        usageCount: 23
      }
    ];
    
    return createApiResponse(templates, 'Resolution templates retrieved successfully');
  }

  async getResolutionQuality() {
    await this.simulateDelay();
    
    const quality = {
      lastUpdated: new Date().toISOString(),
      metrics: [
        {
          name: 'Resolution Accuracy',
          value: '94%',
          status: 'good',
          percentage: 94
        },
        {
          name: 'Customer Satisfaction',
          value: '4.2/5',
          status: 'good',
          percentage: 84
        },
        {
          name: 'First Call Resolution',
          value: '78%',
          status: 'warning',
          percentage: 78
        },
        {
          name: 'Resolution Time',
          value: '2.3 hours',
          status: 'good',
          percentage: 85
        },
        {
          name: 'Follow-up Required',
          value: '12%',
          status: 'good',
          percentage: 88
        },
        {
          name: 'Escalation Rate',
          value: '8%',
          status: 'warning',
          percentage: 80
        }
      ]
    };
    
    return createApiResponse(quality, 'Resolution quality retrieved successfully');
  }

  async getResolutionWorkflow() {
    await this.simulateDelay();
    
    const workflow = [
      {
        id: 'step-001',
        name: 'Initial Assessment',
        description: 'Analyze the ticket and understand the issue',
        status: 'completed',
        completed: true
      },
      {
        id: 'step-002',
        name: 'Research Solution',
        description: 'Research and identify potential solutions',
        status: 'completed',
        completed: true
      },
      {
        id: 'step-003',
        name: 'Implement Solution',
        description: 'Apply the identified solution',
        status: 'in_progress',
        completed: false
      },
      {
        id: 'step-004',
        name: 'Test Resolution',
        description: 'Verify that the solution works correctly',
        status: 'pending',
        completed: false
      },
      {
        id: 'step-005',
        name: 'Document Resolution',
        description: 'Document the solution and update knowledge base',
        status: 'pending',
        completed: false
      },
      {
        id: 'step-006',
        name: 'Customer Follow-up',
        description: 'Follow up with customer to ensure satisfaction',
        status: 'pending',
        completed: false
      }
    ];
    
    return createApiResponse(workflow, 'Resolution workflow retrieved successfully');
  }

  async createResolution(resolutionData) {
    await this.simulateDelay();
    
    const result = {
      ...resolutionData,
      id: `resolution-${Date.now()}`,
      createdAt: new Date().toISOString(),
      createdBy: 'current_agent',
      status: 'completed'
    };
    
    return createApiResponse(result, 'Resolution created successfully');
  }

  // Knowledge Base Methods
  async getKnowledgeMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Total Articles',
          value: '1,247',
          status: 'good',
          trend: 'up',
          change: '+89'
        },
        {
          name: 'Published Articles',
          value: '1,156',
          status: 'good',
          trend: 'up',
          change: '+67'
        },
        {
          name: 'Draft Articles',
          value: '91',
          status: 'warning',
          trend: 'up',
          change: '+12'
        },
        {
          name: 'Monthly Views',
          value: '45,678',
          status: 'good',
          trend: 'up',
          change: '+3,245'
        }
      ],
      categoryDistribution: [
        {
          category: 'technical',
          name: 'Technical Support',
          count: 456,
          percentage: 36.6
        },
        {
          category: 'account',
          name: 'Account Management',
          count: 312,
          percentage: 25.0
        },
        {
          category: 'billing',
          name: 'Billing & Payments',
          count: 234,
          percentage: 18.8
        },
        {
          category: 'general',
          name: 'General Information',
          count: 245,
          percentage: 19.6
        }
      ]
    };
    
    return createApiResponse(metrics, 'Knowledge metrics retrieved successfully');
  }

  async getKnowledgeStats() {
    await this.simulateDelay();
    
    const stats = {
      lastUpdated: new Date().toISOString(),
      analyticsMetrics: [
        {
          name: 'Article Views',
          value: '45,678',
          status: 'good',
          percentage: 95
        },
        {
          name: 'Search Success Rate',
          value: '78%',
          status: 'good',
          percentage: 78
        },
        {
          name: 'Article Helpfulness',
          value: '4.2/5',
          status: 'good',
          percentage: 84
        },
        {
          name: 'Content Freshness',
          value: '85%',
          status: 'warning',
          percentage: 85
        },
        {
          name: 'User Satisfaction',
          value: '4.1/5',
          status: 'good',
          percentage: 82
        },
        {
          name: 'Search Queries',
          value: '12,456',
          status: 'good',
          percentage: 88
        }
      ]
    };
    
    return createApiResponse(stats, 'Knowledge stats retrieved successfully');
  }

  async getRecentArticles() {
    await this.simulateDelay();
    
    const articles = [
      {
        id: 'kb-001',
        title: 'How to reset your password',
        category: 'account',
        status: 'published',
        updatedAt: '2 hours ago'
      },
      {
        id: 'kb-002',
        title: 'Email configuration guide',
        category: 'technical',
        status: 'published',
        updatedAt: '4 hours ago'
      },
      {
        id: 'kb-003',
        title: 'Billing and payment methods',
        category: 'billing',
        status: 'draft',
        updatedAt: '6 hours ago'
      },
      {
        id: 'kb-004',
        title: 'Software installation troubleshooting',
        category: 'technical',
        status: 'review',
        updatedAt: '8 hours ago'
      },
      {
        id: 'kb-005',
        title: 'Account security best practices',
        category: 'account',
        status: 'published',
        updatedAt: '12 hours ago'
      }
    ];
    
    return createApiResponse(articles, 'Recent articles retrieved successfully');
  }

  async getPopularArticles() {
    await this.simulateDelay();
    
    const articles = [
      {
        id: 'kb-001',
        title: 'How to reset your password',
        description: 'Step-by-step guide for resetting your account password',
        category: 'account',
        status: 'published',
        views: 1250,
        helpful: 89,
        updatedAt: '2 hours ago'
      },
      {
        id: 'kb-002',
        title: 'Email configuration guide',
        description: 'Complete guide for setting up email clients',
        category: 'technical',
        status: 'published',
        views: 890,
        helpful: 76,
        updatedAt: '4 hours ago'
      },
      {
        id: 'kb-003',
        title: 'Billing and payment methods',
        description: 'Information about billing cycles and payment options',
        category: 'billing',
        status: 'draft',
        views: 0,
        helpful: 0,
        updatedAt: '6 hours ago'
      },
      {
        id: 'kb-004',
        title: 'Software installation troubleshooting',
        description: 'Common issues and solutions for software installation',
        category: 'technical',
        status: 'review',
        views: 0,
        helpful: 0,
        updatedAt: '8 hours ago'
      },
      {
        id: 'kb-005',
        title: 'Account security best practices',
        description: 'Tips for keeping your account secure',
        category: 'account',
        status: 'published',
        views: 567,
        helpful: 45,
        updatedAt: '12 hours ago'
      },
      {
        id: 'kb-006',
        title: 'Network connectivity issues',
        description: 'Troubleshooting network connection problems',
        category: 'technical',
        status: 'published',
        views: 423,
        helpful: 34,
        updatedAt: '1 day ago'
      }
    ];
    
    return createApiResponse(articles, 'Popular articles retrieved successfully');
  }

  async getKnowledgeCategories() {
    await this.simulateDelay();
    
    const categories = [
      {
        id: 'technical',
        name: 'Technical Support',
        description: 'Technical issues and troubleshooting guides',
        articleCount: 456
      },
      {
        id: 'account',
        name: 'Account Management',
        description: 'Account settings, security, and user management',
        articleCount: 312
      },
      {
        id: 'billing',
        name: 'Billing & Payments',
        description: 'Billing information and payment methods',
        articleCount: 234
      },
      {
        id: 'general',
        name: 'General Information',
        description: 'General information and frequently asked questions',
        articleCount: 245
      }
    ];
    
    return createApiResponse(categories, 'Knowledge categories retrieved successfully');
  }

  async searchKnowledgeBase(query) {
    await this.simulateDelay();
    
    const results = [
      {
        id: 'kb-001',
        title: 'How to reset your password',
        description: 'Step-by-step guide for resetting your account password',
        category: 'account',
        relevance: 95
      },
      {
        id: 'kb-002',
        title: 'Email configuration guide',
        description: 'Complete guide for setting up email clients',
        category: 'technical',
        relevance: 87
      }
    ];
    
    return createApiResponse(results, 'Search results retrieved successfully');
  }

  // Article Management Methods
  async getArticles() {
    await this.simulateDelay();
    
    const articles = [
      {
        id: 'kb-001',
        title: 'How to reset your password',
        content: 'Step-by-step guide for resetting your account password...',
        category: 'account',
        status: 'published',
        views: 1250,
        helpful: 89,
        createdAt: '2024-01-10',
        updatedAt: '2 hours ago',
        author: 'John Doe',
        tags: ['password', 'security', 'account']
      },
      {
        id: 'kb-002',
        title: 'Email configuration guide',
        content: 'Complete guide for setting up email clients...',
        category: 'technical',
        status: 'published',
        views: 890,
        helpful: 76,
        createdAt: '2024-01-08',
        updatedAt: '4 hours ago',
        author: 'Jane Smith',
        tags: ['email', 'configuration', 'setup']
      },
      {
        id: 'kb-003',
        title: 'Billing and payment methods',
        content: 'Information about billing cycles and payment options...',
        category: 'billing',
        status: 'draft',
        views: 0,
        helpful: 0,
        createdAt: '2024-01-12',
        updatedAt: '6 hours ago',
        author: 'Mike Johnson',
        tags: ['billing', 'payment', 'subscription']
      },
      {
        id: 'kb-004',
        title: 'Software installation troubleshooting',
        content: 'Common issues and solutions for software installation...',
        category: 'technical',
        status: 'review',
        views: 0,
        helpful: 0,
        createdAt: '2024-01-11',
        updatedAt: '8 hours ago',
        author: 'Sarah Wilson',
        tags: ['software', 'installation', 'troubleshooting']
      },
      {
        id: 'kb-005',
        title: 'Account security best practices',
        content: 'Tips for keeping your account secure...',
        category: 'account',
        status: 'published',
        views: 567,
        helpful: 45,
        createdAt: '2024-01-09',
        updatedAt: '12 hours ago',
        author: 'David Brown',
        tags: ['security', 'best-practices', 'account']
      }
    ];
    
    return createApiResponse(articles, 'Articles retrieved successfully');
  }

  async getArticleMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Total Articles',
          value: '1,247',
          status: 'good'
        },
        {
          name: 'Published Articles',
          value: '1,156',
          status: 'good'
        },
        {
          name: 'Draft Articles',
          value: '91',
          status: 'warning'
        },
        {
          name: 'Articles in Review',
          value: '45',
          status: 'warning'
        }
      ]
    };
    
    return createApiResponse(metrics, 'Article metrics retrieved successfully');
  }

  async createArticle(articleData) {
    await this.simulateDelay();
    
    const result = {
      ...articleData,
      id: `kb-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: 'Just now',
      author: 'Current Agent',
      views: 0,
      helpful: 0,
      tags: []
    };
    
    return createApiResponse(result, 'Article created successfully');
  }

  async updateArticle(articleId, articleData) {
    await this.simulateDelay();
    
    const result = {
      ...articleData,
      id: articleId,
      updatedAt: 'Just now',
      updatedBy: 'Current Agent'
    };
    
    return createApiResponse(result, 'Article updated successfully');
  }

  async deleteArticle(articleId) {
    await this.simulateDelay();
    
    return createApiResponse({ id: articleId }, 'Article deleted successfully');
  }

  async getArticleVersions(articleId) {
    await this.simulateDelay();
    
    const versions = [
      {
        id: 'v1',
        version: '1.0',
        content: 'Initial version of the article',
        createdAt: '2024-01-10',
        author: 'John Doe',
        changes: 'Initial creation'
      },
      {
        id: 'v2',
        version: '1.1',
        content: 'Updated content with additional information',
        createdAt: '2024-01-12',
        author: 'Jane Smith',
        changes: 'Added troubleshooting section'
      }
    ];
    
    return createApiResponse(versions, 'Article versions retrieved successfully');
  }

  async publishArticle(articleId) {
    await this.simulateDelay();
    
    return createApiResponse({ id: articleId, status: 'published' }, 'Article published successfully');
  }

  async archiveArticle(articleId) {
    await this.simulateDelay();
    
    return createApiResponse({ id: articleId, status: 'archived' }, 'Article archived successfully');
  }

  // Problem Management Methods
  async getProblems() {
    await this.simulateDelay();

    const problems = [
      {
        id: 'prob-001',
        title: 'Email server connectivity issues',
        description: 'Users unable to send emails due to server connectivity problems',
        status: 'open',
        priority: 'high',
        createdAt: '2024-01-15',
        assignedTo: 'John Doe',
        category: 'infrastructure'
      },
      {
        id: 'prob-002',
        title: 'Database performance degradation',
        description: 'Slow query performance affecting system responsiveness',
        status: 'investigating',
        priority: 'medium',
        createdAt: '2024-01-14',
        assignedTo: 'Jane Smith',
        category: 'performance'
      },
      {
        id: 'prob-003',
        title: 'User authentication failures',
        description: 'Intermittent login failures for some users',
        status: 'resolved',
        priority: 'high',
        createdAt: '2024-01-13',
        assignedTo: 'Mike Johnson',
        category: 'security'
      },
      {
        id: 'prob-004',
        title: 'File upload timeout errors',
        description: 'Large file uploads timing out after 30 seconds',
        status: 'closed',
        priority: 'low',
        createdAt: '2024-01-12',
        assignedTo: 'Sarah Wilson',
        category: 'functionality'
      }
    ];

    return createApiResponse(problems, 'Problems retrieved successfully');
  }

  async getLinkedIncidents() {
    await this.simulateDelay();

    const linkedIncidents = [
      {
        id: 'link-001',
        incidentId: 'INC-1024',
        problemId: 'prob-001',
        incidentTitle: 'Email outage for APAC region',
        problemTitle: 'Email server connectivity issues',
        status: 'active',
        priority: 'critical',
        impact: 'High',
        affectedUsers: 340,
        assignedTo: 'John Doe',
        createdAt: '2024-01-14T08:30:00Z',
        lastUpdated: '2024-01-15T04:15:00Z',
        slaBreachRisk: true,
        customer: 'Globex Corp',
        services: ['Email', 'Collaboration'],
        notes: 'Awaiting hardware replacement ETA from vendor.'
      },
      {
        id: 'link-002',
        incidentId: 'INC-1051',
        problemId: 'prob-002',
        incidentTitle: 'Database latency affecting analytics',
        problemTitle: 'Database performance degradation',
        status: 'investigating',
        priority: 'high',
        impact: 'Medium',
        affectedUsers: 95,
        assignedTo: 'Jane Smith',
        createdAt: '2024-01-13T11:05:00Z',
        lastUpdated: '2024-01-14T19:45:00Z',
        slaBreachRisk: false,
        customer: 'Initech',
        services: ['Analytics Platform'],
        notes: 'Index optimization in progress.'
      },
      {
        id: 'link-003',
        incidentId: 'INC-1012',
        problemId: 'prob-003',
        incidentTitle: 'User login failures (sporadic)',
        problemTitle: 'User authentication failures',
        status: 'resolved',
        priority: 'medium',
        impact: 'Low',
        affectedUsers: 42,
        assignedTo: 'Mike Johnson',
        createdAt: '2024-01-10T06:50:00Z',
        lastUpdated: '2024-01-12T09:25:00Z',
        slaBreachRisk: false,
        customer: 'ACME Corp',
        services: ['Identity Service'],
        notes: 'Patched authentication service; monitoring for 48 hours.'
      },
      {
        id: 'link-004',
        incidentId: 'INC-1088',
        problemId: 'prob-004',
        incidentTitle: 'Large file uploads timing out',
        problemTitle: 'File upload timeout errors',
        status: 'active',
        priority: 'medium',
        impact: 'Medium',
        affectedUsers: 60,
        assignedTo: 'Sarah Wilson',
        createdAt: '2024-01-15T14:10:00Z',
        lastUpdated: '2024-01-15T16:20:00Z',
        slaBreachRisk: false,
        customer: 'Hooli',
        services: ['File Storage'],
        notes: 'Investigating possible CDN misconfiguration.'
      }
    ];

    return createApiResponse(linkedIncidents, 'Linked incidents retrieved successfully');
  }

  // Root Cause Analysis Methods
  async getRootCauseAnalyses() {
    await this.simulateDelay();

    const analyses = [
      {
        id: 'rca-001',
        problemId: 'prob-001',
        title: 'Email server outages due to failing HA proxy',
        status: 'analysis',
        severity: 'critical',
        impact: 'High - 300+ users affected',
        owner: 'John Doe',
        createdAt: '2024-01-14T09:10:00Z',
        updatedAt: '2024-01-15T07:45:00Z',
        cause: 'Intermittent HAProxy failovers caused session drops',
        contributingFactors: ['Outdated firmware', 'Misconfigured health checks'],
        evidenceCount: 6,
        proposedFix: 'Upgrade firmware and adjust health check thresholds',
        fixETA: '2024-01-18',
        relatedIncidents: 4,
        tags: ['email', 'infrastructure', 'haproxy']
      },
      {
        id: 'rca-002',
        problemId: 'prob-002',
        title: 'Slow DB queries from missing indexes',
        status: 'proposed_fix',
        severity: 'high',
        impact: 'Medium - analytics reports delayed',
        owner: 'Jane Smith',
        createdAt: '2024-01-13T10:30:00Z',
        updatedAt: '2024-01-14T19:00:00Z',
        cause: 'Critical reports lacked composite indexes',
        contributingFactors: ['Query pattern changes', 'Growth in data size'],
        evidenceCount: 3,
        proposedFix: 'Add composite indexes and update query planner stats',
        fixETA: '2024-01-17',
        relatedIncidents: 2,
        tags: ['database', 'performance']
      },
      {
        id: 'rca-003',
        problemId: 'prob-003',
        title: 'Auth failures from token expiration skew',
        status: 'validated',
        severity: 'medium',
        impact: 'Low - sporadic user failures',
        owner: 'Mike Johnson',
        createdAt: '2024-01-10T07:00:00Z',
        updatedAt: '2024-01-12T10:10:00Z',
        cause: 'Clock drift on one auth node',
        contributingFactors: ['NTP misconfiguration'],
        evidenceCount: 5,
        proposedFix: 'Reconfigure NTP + add drift alerts',
        fixETA: '2024-01-12',
        relatedIncidents: 1,
        tags: ['identity', 'ntp']
      }
    ];

    return createApiResponse(analyses, 'Root cause analyses retrieved successfully');
  }

  async getRootCauseForProblem(problemId) {
    await this.simulateDelay();
    const all = (await this.getRootCauseAnalyses()).data || [];
    const found = Array.isArray(all) ? all.find(a => a.problemId === problemId) : null;
    return createApiResponse(found || null, found ? 'Root cause found' : 'No root cause for problem');
  }

  async updateRootCauseStatus(rcaId, status) {
    await this.simulateDelay();
    return createApiResponse({ id: rcaId, status, updatedAt: new Date().toISOString() }, 'Root cause status updated');
  }

  async getProblemMetrics() {
    await this.simulateDelay();
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        { name: 'Total Problems', value: '12', status: 'warning' },
        { name: 'Investigating', value: '8', status: 'warning' },
        { name: 'Resolved Today', value: '5', status: 'good' },
        { name: 'Avg Resolution Time', value: '2.5 days', status: 'good' }
      ]
    };
    return createApiResponse(metrics, 'Problem metrics retrieved successfully');
  }

  async createProblem(problemData) {
    await this.simulateDelay();
    
    const result = {
      ...problemData,
      id: `prob-${Date.now()}`,
      createdAt: new Date().toISOString(),
      status: 'open',
      assignedTo: 'Current Agent'
    };
    
    return createApiResponse(result, 'Problem created successfully');
  }

  async updateProblem(problemId, problemData) {
    await this.simulateDelay();
    
    const result = {
      ...problemData,
      id: problemId,
      updatedAt: new Date().toISOString(),
      updatedBy: 'Current Agent'
    };
    
    return createApiResponse(result, 'Problem updated successfully');
  }

  async deleteProblem(problemId) {
    await this.simulateDelay();
    
    return createApiResponse({ id: problemId }, 'Problem deleted successfully');
  }

  async resolveProblem(problemId) {
    await this.simulateDelay();
    
    return createApiResponse({ id: problemId, status: 'resolved' }, 'Problem resolved successfully');
  }

  // Agent Reports Methods
  async getAgentReports() {
    await this.simulateDelay();
    
    const reports = [
      {
        id: 'report-001',
        name: 'Performance Summary',
        description: 'Overall performance metrics and KPIs',
        type: 'excellent',
        lastGenerated: '2024-01-15',
        format: 'PDF'
      },
      {
        id: 'report-002',
        name: 'Ticket Resolution Report',
        description: 'Detailed ticket resolution statistics',
        type: 'good',
        lastGenerated: '2024-01-14',
        format: 'Excel'
      },
      {
        id: 'report-003',
        name: 'Customer Satisfaction',
        description: 'Customer feedback and satisfaction scores',
        type: 'good',
        lastGenerated: '2024-01-13',
        format: 'PDF'
      },
      {
        id: 'report-004',
        name: 'Knowledge Base Usage',
        description: 'Knowledge base article usage and effectiveness',
        type: 'average',
        lastGenerated: '2024-01-12',
        format: 'Excel'
      },
      {
        id: 'report-005',
        name: 'Problem Management',
        description: 'Problem tracking and resolution metrics',
        type: 'good',
        lastGenerated: '2024-01-11',
        format: 'PDF'
      }
    ];
    
    return createApiResponse(reports, 'Agent reports retrieved successfully');
  }

  async getAgentReportMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Overall Performance',
          value: '94%',
          status: 'excellent',
          percentage: 94
        },
        {
          name: 'Customer Satisfaction',
          value: '4.2/5',
          status: 'good',
          percentage: 84
        },
        {
          name: 'Resolution Rate',
          value: '89%',
          status: 'good',
          percentage: 89
        },
        {
          name: 'Response Time',
          value: '15 min',
          status: 'excellent',
          percentage: 95
        }
      ]
    };
    
    return createApiResponse(metrics, 'Agent report metrics retrieved successfully');
  }

  async generateReport(reportType) {
    await this.simulateDelay();
    
    const result = {
      id: `report-${Date.now()}`,
      type: reportType,
      status: 'generated',
      generatedAt: new Date().toISOString(),
      downloadUrl: `/reports/${reportType}-${Date.now()}.pdf`
    };
    
    return createApiResponse(result, 'Report generated successfully');
  }

  async downloadReport(reportId) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      id: reportId, 
      downloadUrl: `/reports/${reportId}.pdf` 
    }, 'Report download initiated');
  }

  // Performance Management Methods
  async getPerformanceGoals() {
    await this.simulateDelay();
    
    const goals = [
      {
        id: 'goal-001',
        title: 'Increase Ticket Resolution Rate',
        description: 'Achieve 95% ticket resolution rate within SLA',
        target: '95%',
        progress: 87,
        status: 'on_track',
        dueDate: '2024-03-31',
        createdAt: '2024-01-01',
        category: 'productivity'
      },
      {
        id: 'goal-002',
        title: 'Improve Customer Satisfaction',
        description: 'Maintain customer satisfaction rating above 4.5/5',
        target: '4.5/5',
        progress: 92,
        status: 'achieved',
        dueDate: '2024-02-28',
        createdAt: '2024-01-01',
        category: 'customer_service'
      },
      {
        id: 'goal-003',
        title: 'Reduce Response Time',
        description: 'Decrease average response time to under 10 minutes',
        target: '10 min',
        progress: 75,
        status: 'at_risk',
        dueDate: '2024-04-30',
        createdAt: '2024-01-15',
        category: 'efficiency'
      },
      {
        id: 'goal-004',
        title: 'Knowledge Base Contributions',
        description: 'Create 20 new knowledge base articles',
        target: '20 articles',
        progress: 60,
        status: 'on_track',
        dueDate: '2024-06-30',
        createdAt: '2024-01-10',
        category: 'knowledge_sharing'
      }
    ];
    
    return createApiResponse(goals, 'Performance goals retrieved successfully');
  }

  async getPerformanceReviews() {
    await this.simulateDelay();
    
    const reviews = [
      {
        id: 'review-001',
        title: 'Q4 2023 Performance Review',
        period: 'October - December 2023',
        overallRating: 4.3,
        communicationRating: 4.5,
        problemSolvingRating: 4.2,
        customerServiceRating: 4.4,
        feedback: 'Excellent performance this quarter. Strong communication skills and customer service focus. Continue working on problem-solving efficiency.',
        reviewedBy: 'Sarah Johnson',
        reviewDate: '2024-01-15',
        status: 'completed'
      },
      {
        id: 'review-002',
        title: 'Q3 2023 Performance Review',
        period: 'July - September 2023',
        overallRating: 4.1,
        communicationRating: 4.0,
        problemSolvingRating: 4.3,
        customerServiceRating: 4.2,
        feedback: 'Good performance with room for improvement in communication. Strong technical skills demonstrated.',
        reviewedBy: 'Mike Davis',
        reviewDate: '2023-10-15',
        status: 'completed'
      }
    ];
    
    return createApiResponse(reviews, 'Performance reviews retrieved successfully');
  }

  async getPerformanceMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Overall Performance',
          value: '4.3/5',
          status: 'excellent',
          percentage: 86
        },
        {
          name: 'Goal Achievement',
          value: '87%',
          status: 'good',
          percentage: 87
        },
        {
          name: 'Customer Rating',
          value: '4.2/5',
          status: 'good',
          percentage: 84
        },
        {
          name: 'Team Ranking',
          value: '#3',
          status: 'good',
          percentage: 75
        }
      ]
    };
    
    return createApiResponse(metrics, 'Performance metrics retrieved successfully');
  }

  async createPerformanceGoal(goalData) {
    await this.simulateDelay();
    
    const result = {
      ...goalData,
      id: `goal-${Date.now()}`,
      progress: 0,
      status: 'on_track',
      createdAt: new Date().toISOString(),
      createdBy: 'Current Agent'
    };
    
    return createApiResponse(result, 'Performance goal created successfully');
  }

  async updatePerformanceGoal(goalId, goalData) {
    await this.simulateDelay();
    
    const result = {
      ...goalData,
      id: goalId,
      updatedAt: new Date().toISOString(),
      updatedBy: 'Current Agent'
    };
    
    return createApiResponse(result, 'Performance goal updated successfully');
  }

  async deletePerformanceGoal(goalId) {
    await this.simulateDelay();
    
    return createApiResponse({ id: goalId }, 'Performance goal deleted successfully');
  }

  // Training Management Methods
  async getTrainingPrograms() {
    await this.simulateDelay();
    
    const programs = [
      {
        id: 'prog-001',
        title: 'Customer Service Excellence',
        description: 'Learn advanced customer service techniques and best practices',
        category: 'Customer Service',
        status: 'in_progress',
        progress: 65,
        duration: '4 hours',
        rating: 4.5,
        modules: 8,
        completedModules: 5
      },
      {
        id: 'prog-002',
        title: 'Technical Troubleshooting',
        description: 'Master technical problem-solving methodologies',
        category: 'Technical Skills',
        status: 'not_started',
        progress: 0,
        duration: '6 hours',
        rating: 4.2,
        modules: 12,
        completedModules: 0
      },
      {
        id: 'prog-003',
        title: 'Communication Skills',
        description: 'Improve verbal and written communication abilities',
        category: 'Soft Skills',
        status: 'completed',
        progress: 100,
        duration: '3 hours',
        rating: 4.7,
        modules: 6,
        completedModules: 6
      },
      {
        id: 'prog-004',
        title: 'System Administration',
        description: 'Learn system administration and maintenance procedures',
        category: 'Technical Skills',
        status: 'in_progress',
        progress: 30,
        duration: '8 hours',
        rating: 4.3,
        modules: 15,
        completedModules: 4
      }
    ];
    
    return createApiResponse(programs, 'Training programs retrieved successfully');
  }

  async getTrainingMaterials() {
    await this.simulateDelay();
    
    const materials = [
      {
        id: 'mat-001',
        title: 'Customer Service Handbook',
        description: 'Comprehensive guide to customer service best practices',
        type: 'PDF',
        program: 'Customer Service Excellence',
        size: '2.5 MB',
        downloadUrl: '/materials/customer-service-handbook.pdf'
      },
      {
        id: 'mat-002',
        title: 'Troubleshooting Video Series',
        description: 'Step-by-step troubleshooting video tutorials',
        type: 'Video',
        program: 'Technical Troubleshooting',
        size: '150 MB',
        downloadUrl: '/materials/troubleshooting-videos.mp4'
      },
      {
        id: 'mat-003',
        title: 'Communication Templates',
        description: 'Email and chat response templates',
        type: 'Document',
        program: 'Communication Skills',
        size: '1.2 MB',
        downloadUrl: '/materials/communication-templates.docx'
      },
      {
        id: 'mat-004',
        title: 'System Admin Checklist',
        description: 'Administrative tasks and maintenance checklist',
        type: 'PDF',
        program: 'System Administration',
        size: '800 KB',
        downloadUrl: '/materials/admin-checklist.pdf'
      }
    ];
    
    return createApiResponse(materials, 'Training materials retrieved successfully');
  }

  async getTrainingProgress() {
    await this.simulateDelay();
    
    const progress = [
      {
        id: 'prog-001',
        programTitle: 'Customer Service Excellence',
        moduleTitle: 'Module 6: Handling Difficult Customers',
        progress: 65,
        status: 'in_progress',
        timeSpent: '2h 30m',
        lastActivity: '2024-01-15',
        dueDate: '2024-01-20'
      },
      {
        id: 'prog-002',
        programTitle: 'System Administration',
        moduleTitle: 'Module 4: User Management',
        progress: 30,
        status: 'in_progress',
        timeSpent: '1h 15m',
        lastActivity: '2024-01-14',
        dueDate: '2024-01-25'
      },
      {
        id: 'prog-003',
        programTitle: 'Communication Skills',
        moduleTitle: 'Module 6: Written Communication',
        progress: 100,
        status: 'completed',
        timeSpent: '3h 0m',
        lastActivity: '2024-01-10',
        dueDate: '2024-01-15'
      }
    ];
    
    return createApiResponse(progress, 'Training progress retrieved successfully');
  }

  async getTrainingMetrics() {
    await this.simulateDelay();
    
    const metrics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Programs Completed',
          value: '3',
          status: 'good'
        },
        {
          name: 'Current Progress',
          value: '65%',
          status: 'good'
        },
        {
          name: 'Hours Trained',
          value: '24h',
          status: 'excellent'
        },
        {
          name: 'Certificates Earned',
          value: '2',
          status: 'good'
        }
      ]
    };
    
    return createApiResponse(metrics, 'Training metrics retrieved successfully');
  }

  async startTrainingProgram(programId) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      id: programId, 
      status: 'in_progress',
      startedAt: new Date().toISOString()
    }, 'Training program started successfully');
  }

  async completeTrainingModule(moduleId) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      id: moduleId, 
      status: 'completed',
      completedAt: new Date().toISOString()
    }, 'Training module completed successfully');
  }

  // Profile Management Methods
  async getAgentProfile() {
    await this.simulateDelay();
    
    const profile = {
      id: 'agent-001',
      name: 'John Smith',
      title: 'Senior Support Agent',
      email: 'john.smith@company.com',
      phone: '+1 (555) 123-4567',
      department: 'Support',
      location: 'New York, NY',
      status: 'active',
      avatar: null,
      bio: 'Experienced support agent with 5+ years in customer service',
      skills: ['Customer Service', 'Technical Support', 'Problem Solving'],
      languages: ['English', 'Spanish'],
      timezone: 'America/New_York',
      workingHours: '9:00 AM - 5:00 PM EST',
      stats: {
        ticketsResolved: 1247,
        averageRating: 4.8,
        responseTime: '2.3 min',
        memberSince: '2020'
      },
      createdAt: '2020-01-15',
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(profile, 'Agent profile retrieved successfully');
  }

  async updateAgentProfile(profileData) {
    await this.simulateDelay();
    
    const updatedProfile = {
      ...profileData,
      id: 'agent-001',
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(updatedProfile, 'Agent profile updated successfully');
  }

  async getProfileAnalytics() {
    await this.simulateDelay();
    
    const analytics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Profile Completeness',
          value: '95%',
          status: 'excellent'
        },
        {
          name: 'Profile Views',
          value: '234',
          status: 'good'
        },
        {
          name: 'Profile Rating',
          value: '4.8/5',
          status: 'excellent'
        }
      ],
      trends: {
        profileViews: [
          { date: '2024-01-01', views: 45 },
          { date: '2024-01-02', views: 52 },
          { date: '2024-01-03', views: 38 },
          { date: '2024-01-04', views: 61 },
          { date: '2024-01-05', views: 48 }
        ],
        ratings: [
          { date: '2024-01-01', rating: 4.7 },
          { date: '2024-01-02', rating: 4.8 },
          { date: '2024-01-03', rating: 4.6 },
          { date: '2024-01-04', rating: 4.9 },
          { date: '2024-01-05', rating: 4.8 }
        ]
      }
    };
    
    return createApiResponse(analytics, 'Profile analytics retrieved successfully');
  }

  async uploadProfileImage(imageData) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      imageUrl: '/uploads/profile-images/agent-001.jpg',
      uploadedAt: new Date().toISOString()
    }, 'Profile image uploaded successfully');
  }

  async updateProfileSettings(settings) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      ...settings,
      updatedAt: new Date().toISOString()
    }, 'Profile settings updated successfully');
  }

  // Settings Management Methods
  async getAgentSettings() {
    await this.simulateDelay();
    
    const settings = {
      id: 'agent-001',
      workingHoursStart: '09:00',
      workingHoursEnd: '17:00',
      timezone: 'America/New_York',
      workingDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      },
      autoAssignTickets: true,
      preferredTicketTypes: 'all',
      maxConcurrentTickets: 5,
      emailNotifications: {
        newTicketAssignments: true,
        ticketUpdates: true,
        slaAlerts: true
      },
      pushNotifications: {
        mobile: true,
        desktop: false
      },
      twoFactorEnabled: false,
      smsBackup: false,
      sessionTimeout: 60,
      rememberMe: true,
      colorTheme: 'light',
      accentColor: 'blue',
      fontSize: 'medium',
      compactMode: false,
      showAvatars: true,
      integrations: {
        slack: false,
        teams: false,
        googleCalendar: false
      },
      createdAt: '2020-01-15',
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(settings, 'Agent settings retrieved successfully');
  }

  async updateAgentSettings(settingsData) {
    await this.simulateDelay();
    
    const updatedSettings = {
      ...settingsData,
      id: 'agent-001',
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(updatedSettings, 'Agent settings updated successfully');
  }

  async changePassword(passwordData) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      success: true,
      changedAt: new Date().toISOString()
    }, 'Password changed successfully');
  }

  async enableTwoFactor(twoFactorData) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      qrCode: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
      secret: 'JBSWY3DPEHPK3PXP',
      backupCodes: ['12345678', '87654321', '11223344', '44332211']
    }, 'Two-factor authentication enabled successfully');
  }

  async disableTwoFactor() {
    await this.simulateDelay();
    
    return createApiResponse({ 
      success: true,
      disabledAt: new Date().toISOString()
    }, 'Two-factor authentication disabled successfully');
  }

  // Help Support Methods
  async getHelpResources() {
    await this.simulateDelay();
    
    const resources = [
      {
        id: 'res-001',
        title: 'Getting Started Guide',
        description: 'Complete guide to using the helpdesk system effectively',
        type: 'tutorial',
        category: 'getting-started',
        duration: '15 min',
        author: 'Support Team',
        url: '/help/getting-started',
        downloadUrl: '/downloads/getting-started.pdf'
      },
      {
        id: 'res-002',
        title: 'Ticket Management Best Practices',
        description: 'Learn how to efficiently manage and resolve tickets',
        type: 'video',
        category: 'tickets',
        duration: '8 min',
        author: 'John Smith',
        url: '/help/ticket-management',
        downloadUrl: '/downloads/ticket-management.mp4'
      },
      {
        id: 'res-003',
        title: 'Knowledge Base Creation',
        description: 'Step-by-step guide to creating effective knowledge articles',
        type: 'article',
        category: 'knowledge-base',
        duration: '12 min',
        author: 'Sarah Johnson',
        url: '/help/knowledge-base',
        downloadUrl: '/downloads/knowledge-base.pdf'
      },
      {
        id: 'res-004',
        title: 'Integration Setup',
        description: 'How to set up third-party integrations',
        type: 'tutorial',
        category: 'integrations',
        duration: '20 min',
        author: 'Tech Team',
        url: '/help/integrations',
        downloadUrl: '/downloads/integrations.pdf'
      },
      {
        id: 'res-005',
        title: 'Common Issues Resolution',
        description: 'Troubleshooting guide for common system issues',
        type: 'document',
        category: 'troubleshooting',
        duration: '10 min',
        author: 'Support Team',
        url: '/help/troubleshooting',
        downloadUrl: '/downloads/troubleshooting.pdf'
      }
    ];
    
    return createApiResponse(resources, 'Help resources retrieved successfully');
  }

  async getHelpDocumentation() {
    await this.simulateDelay();
    
    const documentation = [
      {
        id: 'doc-001',
        title: 'System Administration Guide',
        description: 'Complete guide for system administrators',
        category: 'getting-started',
        lastUpdated: '2024-01-15',
        version: '2.1',
        url: '/docs/admin-guide'
      },
      {
        id: 'doc-002',
        title: 'API Documentation',
        description: 'Complete API reference and examples',
        category: 'integrations',
        lastUpdated: '2024-01-10',
        version: '1.8',
        url: '/docs/api-reference'
      },
      {
        id: 'doc-003',
        title: 'User Management',
        description: 'Guide to managing users and permissions',
        category: 'tickets',
        lastUpdated: '2024-01-12',
        version: '2.0',
        url: '/docs/user-management'
      },
      {
        id: 'doc-004',
        title: 'Security Best Practices',
        description: 'Security guidelines and best practices',
        category: 'troubleshooting',
        lastUpdated: '2024-01-08',
        version: '1.5',
        url: '/docs/security-guide'
      },
      {
        id: 'doc-005',
        title: 'Performance Optimization',
        description: 'Tips for optimizing system performance',
        category: 'knowledge-base',
        lastUpdated: '2024-01-14',
        version: '1.3',
        url: '/docs/performance-guide'
      }
    ];
    
    return createApiResponse(documentation, 'Help documentation retrieved successfully');
  }

  async searchHelpContent(searchTerm) {
    await this.simulateDelay();
    
    // Mock search results based on search term
    const allContent = [
      {
        id: 'search-001',
        title: 'How to create a ticket',
        description: 'Step-by-step guide to creating tickets in the system',
        category: 'tickets',
        type: 'article',
        url: '/help/create-ticket'
      },
      {
        id: 'search-002',
        title: 'Ticket assignment rules',
        description: 'Understanding automatic ticket assignment rules',
        category: 'tickets',
        type: 'document',
        url: '/help/assignment-rules'
      },
      {
        id: 'search-003',
        title: 'Knowledge base management',
        description: 'Managing and organizing knowledge base articles',
        category: 'knowledge-base',
        type: 'tutorial',
        url: '/help/kb-management'
      },
      {
        id: 'search-004',
        title: 'System troubleshooting',
        description: 'Common system issues and their solutions',
        category: 'troubleshooting',
        type: 'guide',
        url: '/help/system-troubleshooting'
      }
    ];
    
    // Filter results based on search term
    const filteredResults = allContent.filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return createApiResponse(filteredResults, 'Search results retrieved successfully');
  }

  async getHelpAnalytics() {
    await this.simulateDelay();
    
    const analytics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Resources Available',
          value: '45',
          status: 'excellent'
        },
        {
          name: 'Documentation Pages',
          value: '128',
          status: 'good'
        },
        {
          name: 'FAQ Articles',
          value: '85',
          status: 'good'
        },
        {
          name: 'Search Success Rate',
          value: '92%',
          status: 'excellent'
        }
      ]
    };
    
    return createApiResponse(analytics, 'Help analytics retrieved successfully');
  }

  async submitSupportTicket(ticketData) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      id: `ticket-${Date.now()}`,
      status: 'open',
      priority: ticketData.priority || 'medium',
      createdAt: new Date().toISOString()
    }, 'Support ticket submitted successfully');
  }

  async getSupportContacts() {
    await this.simulateDelay();
    
    const contacts = {
      liveChat: {
        available: true,
        waitTime: '2 min',
        hours: '24/7'
      },
      phone: {
        number: '+1 (555) 123-4567',
        hours: '9 AM - 6 PM EST',
        available: true
      },
      email: {
        address: 'support@company.com',
        responseTime: '2 hours',
        available: true
      }
    };
    
    return createApiResponse(contacts, 'Support contacts retrieved successfully');
  }

  // Collaboration Methods
  async getCollaborationTeams() {
    await this.simulateDelay();
    
    const teams = [
      {
        id: 'team-001',
        name: 'Support Team Alpha',
        description: 'Primary support team handling customer inquiries',
        priority: 'high',
        memberCount: 8,
        messageCount: 1247,
        lastActive: '2 min ago',
        status: 'active'
      },
      {
        id: 'team-002',
        name: 'Technical Team Beta',
        description: 'Technical support and troubleshooting team',
        priority: 'medium',
        memberCount: 5,
        messageCount: 892,
        lastActive: '15 min ago',
        status: 'active'
      },
      {
        id: 'team-003',
        name: 'Management Team',
        description: 'Management and leadership discussions',
        priority: 'low',
        memberCount: 3,
        messageCount: 156,
        lastActive: '1 hour ago',
        status: 'active'
      },
      {
        id: 'team-004',
        name: 'Training Team',
        description: 'Training and onboarding coordination',
        priority: 'medium',
        memberCount: 4,
        messageCount: 234,
        lastActive: '30 min ago',
        status: 'active'
      }
    ];
    
    return createApiResponse(teams, 'Collaboration teams retrieved successfully');
  }

  async getTeamMessages() {
    await this.simulateDelay();
    
    const messages = [
      {
        id: 'msg-001',
        teamId: 'team-001',
        senderId: 'user-001',
        senderName: 'John Smith',
        content: 'Hey team, I need help with a complex ticket. Anyone available?',
        timestamp: '2 min ago',
        type: 'text'
      },
      {
        id: 'msg-002',
        teamId: 'team-001',
        senderId: 'user-002',
        senderName: 'Sarah Johnson',
        content: 'I can help! What\'s the issue?',
        timestamp: '1 min ago',
        type: 'text'
      },
      {
        id: 'msg-003',
        teamId: 'team-001',
        senderId: 'current-user',
        senderName: 'You',
        content: 'Thanks Sarah! I\'ll send you the details.',
        timestamp: '30 sec ago',
        type: 'text'
      },
      {
        id: 'msg-004',
        teamId: 'team-002',
        senderId: 'user-003',
        senderName: 'Mike Wilson',
        content: 'System maintenance scheduled for tonight at 11 PM',
        timestamp: '1 hour ago',
        type: 'text'
      },
      {
        id: 'msg-005',
        teamId: 'team-002',
        senderId: 'user-004',
        senderName: 'Lisa Brown',
        content: 'Got it, thanks for the heads up!',
        timestamp: '45 min ago',
        type: 'text'
      }
    ];
    
    return createApiResponse(messages, 'Team messages retrieved successfully');
  }

  async sendTeamMessage(messageData) {
    await this.simulateDelay();
    
    const newMessage = {
      id: `msg-${Date.now()}`,
      teamId: messageData.teamId,
      senderId: 'current-user',
      senderName: 'You',
      content: messageData.content,
      timestamp: 'Just now',
      type: messageData.type || 'text',
      createdAt: new Date().toISOString()
    };
    
    return createApiResponse(newMessage, 'Message sent successfully');
  }

  async getOnlineUsers() {
    await this.simulateDelay();
    
    const users = [
      {
        id: 'user-001',
        name: 'John Smith',
        status: 'online',
        lastSeen: 'Online now'
      },
      {
        id: 'user-002',
        name: 'Sarah Johnson',
        status: 'online',
        lastSeen: 'Online now'
      },
      {
        id: 'user-003',
        name: 'Mike Wilson',
        status: 'away',
        lastSeen: '5 min ago'
      },
      {
        id: 'user-004',
        name: 'Lisa Brown',
        status: 'busy',
        lastSeen: '2 min ago'
      },
      {
        id: 'user-005',
        name: 'David Lee',
        status: 'online',
        lastSeen: 'Online now'
      },
      {
        id: 'user-006',
        name: 'Emma Davis',
        status: 'offline',
        lastSeen: '1 hour ago'
      }
    ];
    
    return createApiResponse(users, 'Online users retrieved successfully');
  }

  async getCollaborationAnalytics() {
    await this.simulateDelay();
    
    const analytics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Active Teams',
          value: '4',
          status: 'excellent'
        },
        {
          name: 'Messages Today',
          value: '127',
          status: 'good'
        },
        {
          name: 'Online Members',
          value: '12',
          status: 'excellent'
        },
        {
          name: 'Response Time',
          value: '2.3 min',
          status: 'good'
        }
      ]
    };
    
    return createApiResponse(analytics, 'Collaboration analytics retrieved successfully');
  }

  async createTeamMeeting(meetingData) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      id: `meeting-${Date.now()}`,
      title: meetingData.title,
      scheduledAt: meetingData.scheduledAt,
      duration: meetingData.duration,
      participants: meetingData.participants,
      meetingUrl: `https://meet.company.com/${Date.now()}`,
      createdAt: new Date().toISOString()
    }, 'Team meeting created successfully');
  }

  async shareScreen(screenData) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      sessionId: `screen-${Date.now()}`,
      shareUrl: `https://share.company.com/${Date.now()}`,
      startedAt: new Date().toISOString()
    }, 'Screen sharing started successfully');
  }

  // Mobile Access Methods
  async getMobileDevices() {
    await this.simulateDelay();
    
    const devices = [
      {
        id: 'device-001',
        name: 'iPhone 14 Pro',
        type: 'smartphone',
        model: 'iPhone 14 Pro',
        os: 'iOS 17.2',
        status: 'active',
        lastActive: '2 min ago',
        securityLevel: 'high',
        registeredAt: '2024-01-10',
        lastSync: '1 min ago'
      },
      {
        id: 'device-002',
        name: 'Samsung Galaxy S23',
        type: 'smartphone',
        model: 'Galaxy S23',
        os: 'Android 14',
        status: 'active',
        lastActive: '15 min ago',
        securityLevel: 'high',
        registeredAt: '2024-01-05',
        lastSync: '10 min ago'
      },
      {
        id: 'device-003',
        name: 'iPad Pro',
        type: 'tablet',
        model: 'iPad Pro 12.9"',
        os: 'iPadOS 17.2',
        status: 'inactive',
        lastActive: '2 hours ago',
        securityLevel: 'medium',
        registeredAt: '2023-12-20',
        lastSync: '2 hours ago'
      },
      {
        id: 'device-004',
        name: 'Work Laptop',
        type: 'desktop',
        model: 'MacBook Pro 16"',
        os: 'macOS Sonoma',
        status: 'suspended',
        lastActive: '1 day ago',
        securityLevel: 'low',
        registeredAt: '2023-11-15',
        lastSync: '1 day ago'
      }
    ];
    
    return createApiResponse(devices, 'Mobile devices retrieved successfully');
  }

  async getMobileSettings() {
    await this.simulateDelay();
    
    const settings = {
      id: 'agent-001',
      autoSync: true,
      offlineMode: true,
      dataLimit: 'unlimited',
      apiKey: 'ak_1234567890abcdef',
      apiEndpoint: 'https://api.company.com/mobile/v1',
      biometricAuth: true,
      deviceEncryption: true,
      remoteWipe: true,
      sessionTimeout: 60,
      locationTracking: false,
      pushNotifications: {
        enabled: true,
        ticketUpdates: true,
        teamMessages: true,
        systemAlerts: true
      },
      syncSettings: {
        frequency: 'realtime',
        wifiOnly: false,
        backgroundSync: true
      },
      createdAt: '2020-01-15',
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(settings, 'Mobile settings retrieved successfully');
  }

  async updateMobileSettings(settingsData) {
    await this.simulateDelay();
    
    const updatedSettings = {
      ...settingsData,
      id: 'agent-001',
      updatedAt: new Date().toISOString()
    };
    
    return createApiResponse(updatedSettings, 'Mobile settings updated successfully');
  }

  async performDeviceAction(deviceId, action) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      deviceId,
      action,
      status: 'success',
      performedAt: new Date().toISOString()
    }, `Device ${action} performed successfully`);
  }

  async getMobileAnalytics() {
    await this.simulateDelay();
    
    const analytics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Active Devices',
          value: '3',
          status: 'excellent'
        },
        {
          name: 'Mobile Sessions',
          value: '127',
          status: 'good'
        },
        {
          name: 'Data Usage',
          value: '2.3 GB',
          status: 'good'
        },
        {
          name: 'Sync Success Rate',
          value: '98%',
          status: 'excellent'
        }
      ],
      usageStats: {
        totalSessions: 127,
        averageSessionTime: '12 min',
        dataTransferred: '2.3 GB',
        syncOperations: 1247,
        errorRate: '0.2%'
      },
      performanceMetrics: {
        appLoadTime: '2.3s',
        syncSpeed: '1.8s',
        batteryUsage: 'Low',
        dataUsage: '45 MB'
      }
    };
    
    return createApiResponse(analytics, 'Mobile analytics retrieved successfully');
  }

  async downloadMobileApp() {
    await this.simulateDelay();
    
    return createApiResponse({ 
      downloadUrl: 'https://downloads.company.com/mobile-app',
      version: '2.1.0',
      size: '45 MB',
      platforms: ['iOS', 'Android']
    }, 'Mobile app download link generated successfully');
  }

  async generateQRCode() {
    await this.simulateDelay();
    
    return createApiResponse({ 
      qrCode: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
      expiresAt: new Date(Date.now() + 300000).toISOString() // 5 minutes
    }, 'QR code generated successfully');
  }

  // Integration Tools Methods
  async getIntegrationTools() {
    await this.simulateDelay();
    
    const integrations = [
      {
        id: 'int-001',
        name: 'Salesforce CRM',
        description: 'Customer relationship management integration',
        type: 'api',
        status: 'active',
        endpoint: 'https://api.salesforce.com/v1',
        authType: 'OAuth 2.0',
        lastSync: '2 min ago',
        responseTime: '245ms',
        lastCheck: '1 min ago'
      },
      {
        id: 'int-002',
        name: 'Slack Communication',
        description: 'Team communication and notifications',
        type: 'webhook',
        status: 'active',
        endpoint: 'https://hooks.slack.com/services',
        authType: 'Bearer Token',
        lastSync: '5 min ago',
        responseTime: '180ms',
        lastCheck: '2 min ago'
      },
      {
        id: 'int-003',
        name: 'MySQL Database',
        description: 'Customer data synchronization',
        type: 'database',
        status: 'testing',
        endpoint: 'mysql://db.company.com:3306',
        authType: 'Database Auth',
        lastSync: 'Never',
        responseTime: 'N/A',
        lastCheck: 'Never'
      },
      {
        id: 'int-004',
        name: 'AWS S3 Storage',
        description: 'File storage and backup integration',
        type: 'cloud',
        status: 'active',
        endpoint: 'https://s3.amazonaws.com',
        authType: 'AWS IAM',
        lastSync: '10 min ago',
        responseTime: '320ms',
        lastCheck: '5 min ago'
      },
      {
        id: 'int-005',
        name: 'GitHub Repository',
        description: 'Code repository integration',
        type: 'oauth',
        status: 'inactive',
        endpoint: 'https://api.github.com',
        authType: 'OAuth 2.0',
        lastSync: '1 hour ago',
        responseTime: 'N/A',
        lastCheck: '1 hour ago'
      },
      {
        id: 'int-006',
        name: 'Jira Project Management',
        description: 'Project and issue tracking',
        type: 'rest',
        status: 'error',
        endpoint: 'https://company.atlassian.net',
        authType: 'Basic Auth',
        lastSync: '2 hours ago',
        responseTime: 'N/A',
        lastCheck: '30 min ago'
      }
    ];
    
    return createApiResponse(integrations, 'Integration tools retrieved successfully');
  }

  async getAvailableTools() {
    await this.simulateDelay();
    
    const tools = [
      {
        id: 'tool-001',
        name: 'Data Sync Tool',
        description: 'Synchronize data between systems',
        category: 'data',
        available: true,
        executions: 1247,
        lastUsed: '2 min ago'
      },
      {
        id: 'tool-002',
        name: 'API Validator',
        description: 'Validate API endpoints and responses',
        category: 'automation',
        available: true,
        executions: 892,
        lastUsed: '5 min ago'
      },
      {
        id: 'tool-003',
        name: 'Webhook Test',
        description: 'Test webhook endpoints',
        category: 'communication',
        available: true,
        executions: 456,
        lastUsed: '10 min ago'
      },
      {
        id: 'tool-004',
        name: 'Database Cleanup',
        description: 'Clean up and optimize database',
        category: 'data',
        available: true,
        executions: 234,
        lastUsed: '1 hour ago'
      },
      {
        id: 'tool-005',
        name: 'Security Scanner',
        description: 'Scan for security vulnerabilities',
        category: 'security',
        available: false,
        executions: 0,
        lastUsed: 'Never'
      },
      {
        id: 'tool-006',
        name: 'Performance Monitor',
        description: 'Monitor system performance',
        category: 'monitoring',
        available: true,
        executions: 678,
        lastUsed: '15 min ago'
      },
      {
        id: 'tool-007',
        name: 'Analytics Generator',
        description: 'Generate analytics reports',
        category: 'analytics',
        available: true,
        executions: 345,
        lastUsed: '30 min ago'
      },
      {
        id: 'tool-008',
        name: 'Backup Manager',
        description: 'Manage system backups',
        category: 'data',
        available: true,
        executions: 123,
        lastUsed: '2 hours ago'
      }
    ];
    
    return createApiResponse(tools, 'Available tools retrieved successfully');
  }

  async performIntegrationAction(integrationId, action) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      integrationId,
      action,
      status: 'success',
      performedAt: new Date().toISOString(),
      result: `Integration ${action} completed successfully`
    }, `Integration ${action} performed successfully`);
  }

  async executeTool(toolId, parameters) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      toolId,
      parameters,
      executionId: `exec-${Date.now()}`,
      status: 'success',
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      duration: '2.3s',
      result: 'Tool executed successfully'
    }, 'Tool executed successfully');
  }

  async getIntegrationAnalytics() {
    await this.simulateDelay();
    
    const analytics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Active Integrations',
          value: '4',
          status: 'excellent'
        },
        {
          name: 'Tool Executions',
          value: '3,847',
          status: 'good'
        },
        {
          name: 'Success Rate',
          value: '98.5%',
          status: 'excellent'
        },
        {
          name: 'Avg Response Time',
          value: '245ms',
          status: 'good'
        }
      ],
      usageStats: {
        totalIntegrations: 6,
        activeIntegrations: 4,
        totalToolExecutions: 3847,
        averageExecutionTime: '2.3s',
        errorRate: '1.5%'
      },
      performanceMetrics: {
        averageResponseTime: '245ms',
        successRate: '98.5%',
        errorRate: '1.5%',
        uptime: '99.9%'
      }
    };
    
    return createApiResponse(analytics, 'Integration analytics retrieved successfully');
  }

  async testIntegration(integrationId) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      integrationId,
      testId: `test-${Date.now()}`,
      status: 'success',
      responseTime: '245ms',
      testedAt: new Date().toISOString(),
      result: 'Integration test passed'
    }, 'Integration test completed successfully');
  }

  async configureIntegration(integrationId, config) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      integrationId,
      config,
      status: 'success',
      configuredAt: new Date().toISOString()
    }, 'Integration configured successfully');
  }

  // Workflow Optimization Methods
  async getWorkflowOptimizations() {
    await this.simulateDelay();
    
    const workflows = [
      {
        id: 'wf-001',
        name: 'Ticket Assignment Workflow',
        description: 'Automated ticket assignment and routing',
        type: 'ticket',
        status: 'needs_optimization',
        averageDuration: '3.2 min',
        executions: 1247,
        efficiency: 78,
        trend: 'down',
        trendChange: '-5%',
        lastRun: '2 min ago'
      },
      {
        id: 'wf-002',
        name: 'Escalation Process',
        description: 'Ticket escalation and notification workflow',
        type: 'escalation',
        status: 'optimized',
        averageDuration: '1.8 min',
        executions: 892,
        efficiency: 92,
        trend: 'up',
        trendChange: '+3%',
        lastRun: '5 min ago'
      },
      {
        id: 'wf-003',
        name: 'Approval Workflow',
        description: 'Multi-level approval process',
        type: 'approval',
        status: 'critical',
        averageDuration: '15.6 min',
        executions: 456,
        efficiency: 45,
        trend: 'down',
        trendChange: '-12%',
        lastRun: '10 min ago'
      },
      {
        id: 'wf-004',
        name: 'Notification System',
        description: 'Automated notification distribution',
        type: 'notification',
        status: 'analyzing',
        averageDuration: '0.8 min',
        executions: 2341,
        efficiency: 88,
        trend: 'stable',
        trendChange: '0%',
        lastRun: '1 min ago'
      },
      {
        id: 'wf-005',
        name: 'Data Sync Workflow',
        description: 'Synchronize data across systems',
        type: 'automation',
        status: 'needs_optimization',
        averageDuration: '8.4 min',
        executions: 123,
        efficiency: 65,
        trend: 'down',
        trendChange: '-8%',
        lastRun: '30 min ago'
      },
      {
        id: 'wf-006',
        name: 'Report Generation',
        description: 'Automated report generation and distribution',
        type: 'automation',
        status: 'optimized',
        averageDuration: '4.2 min',
        executions: 678,
        efficiency: 95,
        trend: 'up',
        trendChange: '+2%',
        lastRun: '1 hour ago'
      }
    ];
    
    return createApiResponse(workflows, 'Workflow optimizations retrieved successfully');
  }

  async getOptimizationSuggestions() {
    await this.simulateDelay();
    
    const optimizations = [
      {
        id: 'opt-001',
        title: 'Parallel Processing for Ticket Assignment',
        description: 'Implement parallel processing to reduce ticket assignment time by 40%',
        type: 'performance',
        impact: 'High',
        effort: 'Medium',
        estimatedSavings: '1.2 min per ticket',
        priority: 'high'
      },
      {
        id: 'opt-002',
        title: 'Automate Approval Notifications',
        description: 'Add automated notifications to reduce manual follow-ups',
        type: 'automation',
        impact: 'Medium',
        effort: 'Low',
        estimatedSavings: '5 min per approval',
        priority: 'medium'
      },
      {
        id: 'opt-003',
        title: 'Cache Frequently Used Data',
        description: 'Implement caching for frequently accessed data to improve response times',
        type: 'performance',
        impact: 'High',
        effort: 'High',
        estimatedSavings: '2.1 min per workflow',
        priority: 'high'
      },
      {
        id: 'opt-004',
        title: 'Optimize Database Queries',
        description: 'Optimize database queries in data sync workflow',
        type: 'efficiency',
        impact: 'Medium',
        effort: 'Medium',
        estimatedSavings: '3.5 min per sync',
        priority: 'medium'
      },
      {
        id: 'opt-005',
        title: 'Reduce Resource Allocation',
        description: 'Optimize resource allocation for better performance',
        type: 'resource',
        impact: 'Low',
        effort: 'Low',
        estimatedSavings: '0.8 min per workflow',
        priority: 'low'
      },
      {
        id: 'opt-006',
        title: 'Implement Workflow Templates',
        description: 'Create reusable workflow templates to reduce setup time',
        type: 'efficiency',
        impact: 'Medium',
        effort: 'High',
        estimatedSavings: '10 min per new workflow',
        priority: 'medium'
      }
    ];
    
    return createApiResponse(optimizations, 'Optimization suggestions retrieved successfully');
  }

  async performWorkflowAction(workflowId, action) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      workflowId,
      action,
      status: 'success',
      performedAt: new Date().toISOString(),
      result: `Workflow ${action} completed successfully`
    }, `Workflow ${action} performed successfully`);
  }

  async applyOptimization(optimizationId) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      optimizationId,
      status: 'applied',
      appliedAt: new Date().toISOString(),
      estimatedImpact: 'Performance improvement of 15-25%',
      result: 'Optimization applied successfully'
    }, 'Optimization applied successfully');
  }

  async getWorkflowAnalytics() {
    await this.simulateDelay();
    
    const analytics = {
      lastUpdated: new Date().toISOString(),
      overviewMetrics: [
        {
          name: 'Avg Execution Time',
          value: '4.2 min',
          status: 'good',
          trend: 'down',
          change: '-8%'
        },
        {
          name: 'Success Rate',
          value: '94.2%',
          status: 'excellent',
          trend: 'up',
          change: '+2%'
        },
        {
          name: 'Efficiency Score',
          value: '78%',
          status: 'good',
          trend: 'up',
          change: '+5%'
        },
        {
          name: 'Optimization Score',
          value: 'B+',
          status: 'good',
          trend: 'up',
          change: '+1 grade'
        }
      ],
      performanceStats: {
        totalWorkflows: 6,
        optimizedWorkflows: 2,
        needsOptimization: 3,
        criticalWorkflows: 1,
        averageEfficiency: 78,
        totalExecutions: 6337
      },
      trends: {
        executionTime: 'decreasing',
        successRate: 'increasing',
        efficiency: 'improving',
        resourceUsage: 'stable'
      }
    };
    
    return createApiResponse(analytics, 'Workflow analytics retrieved successfully');
  }

  async analyzeWorkflow(workflowId) {
    await this.simulateDelay();
    
    return createApiResponse({ 
      workflowId,
      analysisId: `analysis-${Date.now()}`,
      status: 'completed',
      analyzedAt: new Date().toISOString(),
      findings: [
        'Bottleneck identified in step 3',
        'Resource utilization at 85%',
        'Opportunity for parallel processing',
        'Cache miss rate: 12%'
      ],
      recommendations: [
        'Implement parallel processing',
        'Add caching layer',
        'Optimize database queries',
        'Reduce resource allocation'
      ]
    }, 'Workflow analysis completed successfully');
  }

  async generateOptimizationReport() {
    await this.simulateDelay();
    
    return createApiResponse({ 
      reportId: `report-${Date.now()}`,
      generatedAt: new Date().toISOString(),
      downloadUrl: '/reports/workflow-optimization.pdf',
      summary: {
        totalWorkflows: 6,
        optimizedCount: 2,
        potentialSavings: '45 min/day',
        recommendations: 6
      }
    }, 'Optimization report generated successfully');
  }
}
